// Tracks the ONE supervisor evaluation the user may have walked away from.
// The report page writes an entry when it starts waiting; the dashboard watcher
// polls it and pops a "your report is ready" toast. Plain localStorage on
// purpose — no global state library, survives navigation and reloads.

export type PendingEval = {
  sessionId: number;
  patient?: string | null;
  caseId?: string | null;
  // Set once the report has landed but the student hasn't acknowledged the toast
  // yet, so navigating to another page re-opens on "hazır" instead of flashing
  // "hazırlanıyor" again while the status is re-checked.
  ready?: boolean;
};

const KEY = "pp_pending_eval";
// Aynı sekmedeki yazım/silme duyurusu (F5): kabuk artık sayfadan sayfaya
// yeniden kurulmadığı için `PendingEvalWatcher` kaydı yalnızca mount'ta değil,
// bu olayla (ve diğer sekmeler için `storage` olayıyla) da yeniden okur.
export const PENDING_EVAL_EVENT = "pp:pending-eval";

function announce(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PENDING_EVAL_EVENT));
}

export function setPendingEval(entry: PendingEval): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* storage unavailable (private mode); the report page still polls */
  }
  announce();
}

export function getPendingEval(): PendingEval | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.sessionId !== "number") return null;
    return parsed as PendingEval;
  } catch {
    return null;
  }
}

/** Clear the entry — optionally only when it matches `sessionId`, so a report
 * page for an older session never wipes a newer pending evaluation. */
export function clearPendingEval(sessionId?: number): void {
  try {
    if (sessionId !== undefined) {
      const current = getPendingEval();
      if (current && current.sessionId !== sessionId) return;
    }
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  announce();
}

/** `useClientValue` aboneliği: diğer sekmeler `storage`, aynı sekme
 * `PENDING_EVAL_EVENT` ile duyurur. Kaydı yalnızca okuyan yüzeyler (sağ üstteki
 * gösterge) ikinci bir yoklayıcı açmadan bununla güncel kalır. */
export function subscribePendingEval(onChange: () => void): () => void {
  window.addEventListener("storage", onChange);
  window.addEventListener(PENDING_EVAL_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(PENDING_EVAL_EVENT, onChange);
  };
}

/** Rapor hâlâ yazılıyor mu: kayıt var ve henüz hazır değil. */
export function isEvalRunning(): boolean {
  const entry = getPendingEval();
  return !!entry && !entry.ready;
}
